import { useRef, useState } from "react";
import { Download, Trash2, Upload } from "lucide-react";

import {
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from "@zentrack/ui";
import { parseBackup, type LocalBackup } from "@zentrack/web-api";

import { useWebApp } from "../app/app-provider";
import { ConfirmDialog } from "./confirm-dialog";
import { downloadText } from "../lib/browser-download";

export function BackupPanel() {
  const { api, perform } = useWebApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingBackup, setPendingBackup] = useState<LocalBackup | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const handleExport = async () => {
    const json = await api.exportBackup();
    const fileName = `zentrack-backup-${new Date().toISOString().slice(0, 10)}.json`;
    downloadText(json, fileName, "application/json");
  };

  const handleFile = async (file: File) => {
    setImportError(null);

    try {
      const text = await file.text();
      const result = parseBackup(text);

      if (!result.ok || !result.backup) {
        setImportError(result.error ?? "This backup could not be read.");
        return;
      }

      setPendingBackup(result.backup);
    } catch {
      setImportError("This backup file could not be read.");
    }
  };

  const handleImportConfirm = async () => {
    if (!pendingBackup) {
      return;
    }

    await perform(async () => {
      await api.importBackup(pendingBackup);
    });
    setPendingBackup(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearConfirm = async () => {
    await perform(async () => {
      await api.clearLocalData();
    });
    setClearConfirmOpen(false);
    toast("Local data cleared");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Backup and restore</CardTitle>
        <CardDescription>
          Export your local data to a JSON file, replace local data by importing a previous
          backup, or clear everything stored in this browser. Imports and clearing overwrite
          everything currently stored here.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void handleExport()} type="button" variant="outline">
            <Download className="size-4" />
            Export JSON backup
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            type="button"
            variant="outline"
          >
            <Upload className="size-4" />
            Import backup
          </Button>
          <Button
            onClick={() => setClearConfirmOpen(true)}
            type="button"
            variant="outline"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-4" />
            Clear local data
          </Button>
          <input
            accept="application/json,.json"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleFile(file);
              }
            }}
            ref={fileInputRef}
            type="file"
          />
        </div>

        {importError ? (
          <Banner tone="error" title="Couldn't import backup" message={importError} />
        ) : null}
      </CardContent>

      <ConfirmDialog
        confirmLabel="Replace local data"
        destructive
        description="This will overwrite all clients, projects, timers, and history currently stored in this browser with the contents of the backup. This cannot be undone."
        onConfirm={handleImportConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setPendingBackup(null);
          }
        }}
        open={Boolean(pendingBackup)}
        title="Replace local data?"
      />

      <ConfirmDialog
        confirmLabel="Clear local data"
        destructive
        description="This will permanently delete all clients, projects, timers, and history stored in this browser. Export a backup first if you want to keep this data. This cannot be undone."
        onConfirm={handleClearConfirm}
        onOpenChange={setClearConfirmOpen}
        open={clearConfirmOpen}
        title="Clear local data?"
      />
    </Card>
  );
}
